import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, Building2, LayoutGrid, MapPin, Search, UserSearch } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useSession } from "@/app/session";
import { ALL_ITEMS } from "@/app/nav";

const TYPE = {
  lead: { label: "Lead", icon: UserSearch },
  customer: { label: "Customer", icon: Building2 },
  machine: { label: "Machine", icon: LayoutGrid },
  product: { label: "Product", icon: Boxes },
  location: { label: "Location", icon: MapPin },
};

// Ctrl/Cmd+K: one box that finds pages, leads, customers, machines, products and QR locations.
const SearchDialog = ({ open, onOpenChange }) => {
  const { api } = useSession();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (!open) { setQ(""); setResults([]); setActive(0); } }, [open]);

  useEffect(() => {
    const term = q.trim();
    const pages = term
      ? ALL_ITEMS.filter((i) => i.label.toLowerCase().includes(term.toLowerCase()))
        .map((i) => ({ type: "page", id: i.to, title: i.label, subtitle: "Go to page", url: i.to, icon: i.icon }))
      : [];
    setResults(pages);
    setActive(0);
    if (term.length < 2) return undefined;
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/app/search", { params: { q: term } })
        .then((res) => { if (live) setResults([...pages, ...res.data.results]); })
        .catch(() => {})
        .finally(() => { if (live) setLoading(false); });
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [q, api]);

  const go = (r) => {
    if (!r) return;
    onOpenChange(false);
    navigate(r.url);
  };

  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); go(results[active]); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[15%] translate-y-0 gap-0 border-white/10 bg-vortex-surface p-0 text-slate-100 sm:max-w-xl"
        onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); }} data-testid="app-search-dialog">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">Find a page, lead, customer, machine, product or location.</DialogDescription>
        <div className="flex items-center gap-3 border-b border-white/10 px-4">
          <Search size={16} className="shrink-0 text-slate-500" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search leads, customers, machines, products..." data-testid="app-search-input"
            className="h-14 w-full bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none" />
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2" data-lenis-prevent>
          {results.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-slate-500">
              {q.trim().length < 2 ? "Type at least two letters." : loading ? "Searching..." : "Nothing found."}
            </p>
          ) : results.map((r, i) => {
            const Icon = r.icon || TYPE[r.type]?.icon || Search;
            return (
              <button key={`${r.type}-${r.id}`} onClick={() => go(r)} onMouseEnter={() => setActive(i)}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left ${i === active ? "bg-vortex-cyan/10" : ""}`}>
                <Icon size={16} className="shrink-0 text-vortex-cyan" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-slate-100">{r.title}</span>
                  {r.subtitle && <span className="block truncate text-xs text-slate-500">{r.subtitle}</span>}
                </span>
                <span className="shrink-0 font-mono text-[10px] tracking-[0.15em] text-slate-500 uppercase">
                  {r.type === "page" ? "Page" : TYPE[r.type]?.label}
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
